import { some } from 'lodash';

import { curriedFind, prop, propEq, compose } from '@core/lib/fp';
import userRoles from '@core/lib/constants/userRoles';

function termsUpdateModal() {
  'ngInject';
  return {
    restrict: 'E',
    scope: {},
    controllerAs: 'vm',
    controller: 'TermsUpdateModalController',
  };
}

function TermsUpdateModalController($scope, $q, $uiRouterGlobals, $transitions, $state, User, Account, Modal) {
  'ngInject';
  const vm = this;

  vm._showTerms = _showTerms;

  vm.$onInit = () => $transitions.onSuccess({}, _init);

  /**
   * @private
   */
  function _init() {
    // We need to remove the modal if user logs out
    if (Modal.getInstance()) {
      Modal.getInstance().cancel();
    }

    $q.all([User.getCurrent(), Account.getCurrent(), Account.getNewTermsStatus()]).then(_initiateModal);
  }

  /**
   * Initialize the Modal based on different conditions
   *
   * @param {Object} user
   * @param {Object} account
   * @param {Object} termsStatus
   * @private
   */
  function _initiateModal([user, account, termsStatus]) {
    const latestTermStatus = curriedFind({
      accepted: false,
    })(termsStatus);
    const isSuperAdmin = user.super_admin;
    const isTermsPage = $uiRouterGlobals.$current.name === 'terms';

    if (!isTermsPage && isSuperAdmin) {
      return;
    }

    if (!isTermsPage && !some(termsStatus, _showTerms)) {
      return;
    }

    const isAdmin = user.role === 'admin';
    const roleLabel = compose(prop('name'), curriedFind(propEq('key', user.role)))(userRoles);
    const accountName = account.name;
    const isDeadlineReached = latestTermStatus.deadline_reached;
    const tooltipMsg = isSuperAdmin ? 'Super admins cannot accept terms' : null;
    const terms = account.vendor === 'adjust Inc' ? 'inc.json' : 'gmbh-kk.json';

    return Account.getTerms(terms).then(({ data }) => {
      Modal.init({
        scope: $scope,
        size: 'medium',
        tpl: require('./terms-update-modal.html'),
        classes: ['terms-update-modal'],
        align: 'center',
        hideConfirm: true,
        hideCancel: true,
        initialModel: {
          isSuperAdmin,
          isAdmin,
          roleLabel,
          accountName,
          isDeadlineReached,
          tooltipMsg,
          termsContent: data,
          copy: _getCopy(latestTermStatus.term_name),
          confirm: () => _onConfirm(latestTermStatus.token),
          cancel: () => _onCancel(latestTermStatus.token),
        },
      }).result.then(() => {
        // In case user is redirected from email and rejects the terms
        // we redirect to apps page, In all other cases we close the modal
        if (isTermsPage) {
          $state.go('apps');
        } else {
          $state.reload();
        }
      });
    });
  }

  function _getCopy(termName) {
    const supportedTerms = {
      gdpr: {
        title: t('terms.dialog.title'),
        titleNonAdmin: t('terms.dialog.title'),
        descriptionAdmin: t('terms.dialog-admin.description'),
        descriptionNonAdmin: t('terms.dialog.description'),
        contactLegal: t('terms.dialog.contact-legal'),
      },
      ccpa: {
        title: t('terms.dialog.title.ccpa'),
        titleNonAdmin: t('terms.dialog.title.non-admin.ccpa'),
        descriptionAdmin: t('terms.dialog-admin.description.ccpa'),
        descriptionNonAdmin: t('terms.dialog.description.ccpa'),
        contactLegal: t('terms.dialog.contact-legal.ccpa'),
      },
    };

    return supportedTerms[termName] || supportedTerms.gdpr;
  }

  /**
   * Check if the terms modals needs to be displayed
   *
   * @param {Object} status
   * @private
   */
  function _showTerms(status) {
    const now = moment().utc();

    if (now.isBefore(moment(status.active_since))) {
      return false;
    }

    if (!status.rejected_at && !status.accepted) {
      return true;
    }

    const rejectedTimeDiff = now.diff(moment(status.rejected_at), 'days');
    const isIgnorable = status.deadline_reached;

    return (isIgnorable || rejectedTimeDiff >= 1) && !status.accepted;
  }

  /**
   * @param {String} token
   * @private
   */
  function _onConfirm(token) {
    Account.acceptNewTerms(token).then(Account.getNewTermsStatusForceUpdate).then(Modal.getInstance().close);
  }

  /**
   * @param {String} token
   * @private
   */
  function _onCancel(token) {
    Account.rejectNewTerms(token).then(Account.getNewTermsStatusForceUpdate).then(Modal.getInstance().close);
  }
}

export { TermsUpdateModalController, termsUpdateModal };
